import { useState, useEffect, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Alert,
  StyledEngineProvider,
  ThemeProvider,
} from "@mui/material";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import mainTheme from "@aclymatepackages/themes";

dayjs.extend(utc);
dayjs.extend(timezone);

const isDateTimeInvalid = (dateTime) => {
  return !dateTime || !dateTime.isValid();
};

const isDateTimeInPast = (dateTime, timezone) => {
  const now = dayjs().tz(timezone);
  return dateTime.isBefore(now);
};

const isDateTimeTooSoon = (dateTime, timezone) => {
  const oneMinuteFromNow = dayjs().tz(timezone).add(1, "minute");
  return dateTime.isBefore(oneMinuteFromNow);
};

const getDateTimeError = (dateTime, timezone) => {
  if (isDateTimeInvalid(dateTime)) {
    return "Please select a valid date and time";
  }
  if (isDateTimeInPast(dateTime, timezone)) {
    return "Please select a future date and time";
  }
  if (isDateTimeTooSoon(dateTime, timezone)) {
    return "Please select a time at least 1 minute in the future";
  }
  return "";
};

const ScheduleSurveyModal = ({
  open,
  onClose,
  onSchedule,
  employeeData,
  companyTimezone = "America/Los_Angeles",
}) => {
  const [selectedDateTime, setSelectedDateTime] = useState(
    dayjs().tz(companyTimezone).add(1, "hour").startOf("hour")
  );
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const validateDateTime = useCallback((dateTime) => {
    const errorMessage = getDateTimeError(dateTime, companyTimezone);
    setError(errorMessage);
    return !errorMessage;
  }, [companyTimezone]);

  useEffect(() => {
    if (open && selectedDateTime) {
      validateDateTime(selectedDateTime);
    }
  }, [open, selectedDateTime, companyTimezone, validateDateTime]);

  const isValidDayjsObject = (value) => {
    return value && dayjs.isDayjs(value);
  };

  const handleValidDateTime = (value) => {
    setSelectedDateTime(value);
    validateDateTime(value);
  };

  const handleClearedDateTime = () => {
    setSelectedDateTime(null);
    setError("Please select a date and time");
  };

  const handleDateTimeChange = (newValue) => {
    if (isValidDayjsObject(newValue)) {
      handleValidDateTime(newValue);
      return;
    }
    if (newValue === null) {
      handleClearedDateTime();
    }
  };

  const hasNoSelectedDateTime = () => {
    return !selectedDateTime;
  };

  const hasDateTimeValidationError = () => {
    return !validateDateTime(selectedDateTime);
  };

  const buildSchedulePayload = () => {
    return {
      scheduledTime: selectedDateTime.toISOString(),
      timezone: companyTimezone,
      employeeData,
    };
  };

  const handleScheduleSuccess = () => {
    onClose();
  };

  const handleScheduleError = () => {
    setError("Failed to schedule survey delivery. Please try again.");
    setIsSubmitting(false);
  };

  const handleSchedule = async () => {
    if (hasNoSelectedDateTime()) {
      setError("Please select a date and time");
      return;
    }

    if (hasDateTimeValidationError()) {
      return;
    }

    setIsSubmitting(true);
    setError("");
    
    try {
      await onSchedule(buildSchedulePayload());
      handleScheduleSuccess();
    } catch (err) {
      handleScheduleError();
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setError("");
      setSelectedDateTime(dayjs().tz(companyTimezone).add(1, "hour").startOf("hour"));
      onClose();
    }
  };

  const minDateTime = dayjs().tz(companyTimezone);

  return (
    <StyledEngineProvider injectFirst>
      <ThemeProvider theme={mainTheme}>
        <Dialog
          open={open}
          onClose={handleClose}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            style: {
              borderRadius: 12,
            },
          }}
        >
          <DialogTitle>
            <Typography variant="h6">Schedule Survey Delivery</Typography>
          </DialogTitle>
          <DialogContent>
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
                Select when you want to send the survey emails to your employees.
                The emails will be sent automatically at the scheduled time.
              </Typography>

              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DateTimePicker
                  label="Schedule Date & Time"
                  value={selectedDateTime}
                  onChange={handleDateTimeChange}
                  disabled={isSubmitting}
                  minDateTime={minDateTime}
                  disablePast
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      error: !!error,
                      helperText: error || `Timezone: ${companyTimezone}`,
                      onBlur: () => {
                        if (selectedDateTime) {
                          validateDateTime(selectedDateTime);
                        }
                      },
                    },
                  }}
                  ampm={true}
                  minutesStep={15}
                  views={["year", "month", "day", "hours", "minutes"]}
                  format="MM/DD/YYYY hh:mm A"
                  shouldDisableTime={(value, view) => {
                    const isTimeView = view === "hours" || view === "minutes";
                    const now = dayjs().tz(companyTimezone);
                    const isSameDay = value && value.isSame(now, "day");
                    const isPastTime = value && value.isBefore(now);
                    
                    return isTimeView && isSameDay && isPastTime;
                  }}
                />
              </LocalizationProvider>

              {error && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {error}
                </Alert>
              )}

              {!error && employeeData && employeeData.length > 0 && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  {`${employeeData.length} employee${
                    employeeData.length > 1 ? "s" : ""
                  } will receive the survey at the scheduled time.`}
                </Alert>
              )}
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button
              onClick={handleClose}
              disabled={isSubmitting}
              color="inherit"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSchedule}
              disabled={isSubmitting || !!error || !selectedDateTime}
              variant="contained"
              color="primary"
            >
              {isSubmitting ? "Scheduling..." : "Schedule Delivery"}
            </Button>
          </DialogActions>
        </Dialog>
      </ThemeProvider>
    </StyledEngineProvider>
  );
};

export default ScheduleSurveyModal;